import { faHeart } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTripContext } from '@/contexts/TripContext';
import { Button } from '@/design-system/primitives/Button';
import { HeartBurst } from '@/design-system/primitives/HeartBurst';
import type { ItineraryItem } from '@/types/itinerary';
import { twx } from '@/utils/tailwind';

export interface AddToTripButtonProps {
  item: ItineraryItem;
  /** 'overlay' — circle button matching the photo overlay style */
  /** 'inline' — compact text+icon button for card link rows */
  variant?: 'overlay' | 'inline';
}

type FAIconStyle = React.CSSProperties & { [key: `--fa-${string}`]: string | undefined };

export const AddToTripButton = ({ item, variant = 'inline' }: AddToTripButtonProps) => {
  const {
    trips,
    addPlace,
    removePlace,
    isInTrip,
    isInAnyTrip,
    navigatePanelToTrip,
    selectedTripId,
  } = useTripContext();

  // Open state controls the remove-confirmation popover that appears
  // when the user taps the heart on a place that's already in the
  // active trip. The add path is one-tap (no popover) — type +
  // scheduling are set later in Curate mode.
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  // Increments on every successful add so the heart icon replays the
  // bounce animation (keyed remount = animation restart). Stable
  // between adds so the heart doesn't re-bounce on every re-render.
  const [bounceCount, setBounceCount] = useState(0);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const savedToAny = isInAnyTrip(item.id);

  // First trip that contains this item, for the label
  const firstTripWithItem = trips.find((t) => isInTrip(t.id, item.id)) ?? null;
  const placeInFirstTrip = firstTripWithItem?.places.find((p) => p.item.id === item.id) ?? null;
  // Describe the place by its trip state: scouted (added but not scheduled)
  // vs curated (scheduled onto a day).
  let tripStatusLabel: string | null = null;
  if (placeInFirstTrip) {
    tripStatusLabel = placeInFirstTrip.scheduledDay
      ? `Curated · Day ${placeInFirstTrip.scheduledDay}`
      : 'Scouted';
  }
  const tripCountLabel =
    trips.filter((t) => isInTrip(t.id, item.id)).length > 1
      ? `+${trips.filter((t) => isInTrip(t.id, item.id)).length - 1} more`
      : null;

  const popoverTrip = selectedTripId ? trips.find((t) => t.id === selectedTripId) : undefined;
  const savedToSelectedTrip = selectedTripId ? isInTrip(selectedTripId, item.id) : false;

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!selectedTripId) return;
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    // Not yet in the trip → add immediately as a generic place. The
    // user picks the itinerary type and schedules in Curate mode; we
    // don't make them do that upfront.
    if (!savedToSelectedTrip) {
      addPlace(selectedTripId, item, 'activity');
      navigatePanelToTrip(selectedTripId);
      setBounceCount((c) => c + 1);
      return;
    }
    // Already in the trip → open the remove-confirmation popover.
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const POPOVER_WIDTH = 256;
      const POPOVER_HEIGHT_ESTIMATE = 96;
      const MARGIN = 8;

      const naturalRight = window.innerWidth - rect.right;
      const naturalLeft = rect.right - POPOVER_WIDTH;
      const style: React.CSSProperties = {
        position: 'fixed',
        width: POPOVER_WIDTH,
        zIndex: 600,
        overflowY: 'auto',
      };

      if (naturalLeft < MARGIN) {
        style.left = Math.max(MARGIN, rect.left);
      } else {
        style.right = Math.max(MARGIN, naturalRight);
      }

      const spaceBelow = window.innerHeight - rect.bottom - MARGIN;
      const spaceAbove = rect.top - MARGIN;
      if (spaceBelow >= POPOVER_HEIGHT_ESTIMATE || spaceBelow >= spaceAbove) {
        style.top = rect.bottom + MARGIN;
        style.maxHeight = Math.min(spaceBelow, POPOVER_HEIGHT_ESTIMATE + 100);
      } else {
        style.bottom = window.innerHeight - rect.top + MARGIN;
        style.maxHeight = Math.min(spaceAbove, POPOVER_HEIGHT_ESTIMATE + 100);
      }

      setMenuStyle(style);
    }
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [isOpen]);

  const handleRemove = () => {
    if (!selectedTripId) return;
    removePlace(selectedTripId, item.id);
    setIsOpen(false);
  };

  const trigger =
    variant === 'overlay' ? (
      <button
        ref={triggerRef}
        type="button"
        onClick={selectedTripId ? handleOpen : undefined}
        disabled={!selectedTripId}
        aria-label={savedToAny ? 'In trip' : 'Add to trip'}
        title={
          savedToAny
            ? `${tripStatusLabel ?? 'In'} · ${firstTripWithItem?.name ?? 'trip'}`
            : 'Add to trip'
        }
        className={twx(
          'w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-md transition-colors',
          !selectedTripId && 'opacity-60',
          savedToAny
            ? 'text-primary-emphasized border-2 border-primary-main/60 hover:bg-primary-subtle'
            : 'text-primary-main border-2 border-content-border/60 hover:bg-white hover:border-primary-main/40',
        )}
      >
        <HeartBurst active={savedToAny}>
          <span
            key={bounceCount}
            className={twx(
              bounceCount > 0 && 'motion-safe:animate-kawaii-heart-bounce inline-block',
            )}
          >
            <FontAwesomeIcon
              icon={faHeart}
              style={
                {
                  width: 15,
                  height: 15,
                  '--fa-primary-color': 'var(--color-primary-emphasized)',
                  '--fa-secondary-color': 'var(--color-primary-emphasized)',
                  '--fa-secondary-opacity': '1',
                } as FAIconStyle
              }
            />
          </span>
        </HeartBurst>
      </button>
    ) : (
      <button
        ref={triggerRef}
        type="button"
        onClick={selectedTripId ? handleOpen : undefined}
        disabled={!selectedTripId}
        aria-label={savedToAny ? 'In trip' : 'Add to trip'}
        className={twx(
          'flex items-center gap-1 text-xs font-medium transition-colors',
          !selectedTripId && 'text-content-disabled',
          savedToAny && selectedTripId
            ? 'text-primary-emphasized'
            : selectedTripId
              ? 'text-content-subtle hover:text-primary-emphasized'
              : '',
        )}
      >
        <HeartBurst active={savedToAny}>
          <FontAwesomeIcon
            icon={faHeart}
            style={
              {
                width: 12,
                height: 12,
                '--fa-primary-color': savedToAny
                  ? 'var(--color-primary-emphasized)'
                  : 'var(--color-primary-main)',
                '--fa-secondary-color': savedToAny
                  ? 'var(--color-primary-emphasized)'
                  : 'var(--color-primary-main)',
                '--fa-secondary-opacity': '1',
              } as FAIconStyle
            }
          />
        </HeartBurst>
        {savedToAny && firstTripWithItem ? (
          <span className="flex items-center gap-1">
            <span className="truncate max-w-[80px]">{firstTripWithItem.name}</span>
            {tripStatusLabel && (
              <span className="flex items-center gap-1 text-content-disabled">
                <span className="text-base leading-none">·</span>
                {tripStatusLabel}
              </span>
            )}
            {tripCountLabel && <span className="text-content-disabled">{tripCountLabel}</span>}
          </span>
        ) : (
          'Add to trip'
        )}
      </button>
    );

  // Only the remove-confirmation path uses the popover now. The add
  // path is one-tap (see handleOpen — `!savedToSelectedTrip` triggers
  // `addPlace` directly without opening anything).
  const popover =
    isOpen && popoverTrip && savedToSelectedTrip
      ? createPortal(
          <div
            ref={popoverRef}
            role="presentation"
            style={menuStyle}
            onClick={(e) => e.stopPropagation()}
            className="bg-white border border-content-border rounded-2xl shadow-xl overflow-hidden"
          >
            <div className="p-3">
              <Button
                size="sm"
                variant="destructive-ghost"
                fullWidth
                className="border border-error-border"
                onClick={handleRemove}
              >
                Remove from {popoverTrip.name}
              </Button>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      {trigger}
      {popover}
    </>
  );
};
